import { ActionMenu, ActionMenuProps } from '@components/ActionMenu';
import { PermissionsModal } from '@modules/securityGroups/components/PermissionsModal/PermissionsModal';
import { AddTask, Delete, Draw, Share } from '@mui/icons-material';
import { ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import { DocumentFragmentFragment, ProjectTask, ProjectTaskType, ShareableResourceType } from 'gql/index';
import { merge } from 'lodash';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { getTenantIdentifier } from '../../../../utils/getTenantIdentifier';
import { TaskFormDrawer } from '../TaskFormDrawer';
import { DeleteTaskDialog } from './DeleteTaskDialog';
import { useProject } from '@modules/projects/utils/useProject';
import { DownloadLinkButton } from '@components/Download/DownloadLinkButton';

interface Props extends Omit<ActionMenuProps, 'open' | 'onOpen'> {
  task: Pick<ProjectTask, 'id' | 'type' | 'name' | 'projectId'> & { document?: Pick<DocumentFragmentFragment, 'id' | 'fileName'> | null};
  hideEdit?: boolean;
  onDeleteSuccess?: () => void;
}

export const TaskActions: React.FC<Props> = ({ task, hideEdit, onDeleteSuccess: onDeleteSuccessProp, ...moreMenuProps }) => {
  const { formatMessage } = useIntl();
  const [isConfirmRemoveDialogOpen, setIsConfirmRemoveDialogOpen] = useState(false);

  const { canManageTasks, isArchived } = useProject({ projectId: task.projectId });

  const [moreMenuDrawerOpen, setMoreMenuDrawerOpen] = useState(false);

  const [isSharingDialogOpen, setIsSharingDialogOpen] = useState(false);

  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);

  const [isAddingSubtaskDrawerOpen, setAddingSubtaskDrawerOpen] = useState(false);

  const onDeleteSuccess = () => {
    setMoreMenuDrawerOpen(false);
    setIsConfirmRemoveDialogOpen(false);
    onDeleteSuccessProp?.();
  };

  if (!canManageTasks || isArchived) {
    return null;
  }

  return <>
    <ActionMenu
      {...moreMenuProps}
      open={moreMenuDrawerOpen}
      onOpen={setMoreMenuDrawerOpen}
      iconButtonProps={merge({}, { color: 'inherit' }, moreMenuProps.iconButtonProps)}
    >
      {!hideEdit && <MenuItem onClick={() => setIsEditDrawerOpen(true)}>
        <ListItemIcon><Draw /></ListItemIcon>
        <ListItemText primary={formatMessage({ id: 'Edit' })} />
      </MenuItem>}

      {task.type == ProjectTaskType.SignDocument && task.document && (
          <DownloadLinkButton variant='MenuItem' color='action' link={`/${getTenantIdentifier()}/api/Documents/${task.document.id}`} fileName={task.document.fileName} />
      )}

      {task.type == ProjectTaskType.Custom && (
        <MenuItem onClick={() => setIsSharingDialogOpen(true)}>
          <ListItemIcon><Share /></ListItemIcon>
          <ListItemText primary={formatMessage({ id: 'Share' })} />
        </MenuItem>
      )}

      {task.type == ProjectTaskType.Custom &&
        <MenuItem onClick={() => setAddingSubtaskDrawerOpen(true)}>
          <ListItemIcon><AddTask /></ListItemIcon>
          <ListItemText primary={formatMessage({ id: 'Add subtask' })} />
        </MenuItem>}

      {task.type == ProjectTaskType.Custom &&
        <MenuItem onClick={() => setIsConfirmRemoveDialogOpen(true)}>
          <ListItemIcon><Delete /></ListItemIcon>
          <ListItemText primary={formatMessage({ id: 'Delete' })} />
        </MenuItem>}
    </ActionMenu>

    <TaskFormDrawer
      taskId={task.id}
      projectId={task.projectId}
      open={isEditDrawerOpen}
      onClose={() => setIsEditDrawerOpen(false)}
    />

    <TaskFormDrawer
      parentTaskId={task.id}
      projectId={task.projectId}
      open={isAddingSubtaskDrawerOpen}
      onClose={() => setAddingSubtaskDrawerOpen(false)}
    />

    <DeleteTaskDialog
      projectId={task.projectId}
      taskId={task.id}
      isOpen={isConfirmRemoveDialogOpen && document != null}
      onCancel={() => setIsConfirmRemoveDialogOpen(false)}
      onDeleteSuccess={onDeleteSuccess}
    />

    <PermissionsModal
      resource={{ resourceType: ShareableResourceType.Task, id: task.id, name: task.name, projectId: task.projectId }}
      open={isSharingDialogOpen}
      onClose={() => setIsSharingDialogOpen(false)}
    />
  </>;
};